import useSWR from 'swr'
import { enrichmentApiHost } from '@crystal-eyes/config'
import useProfileId from './useProfileId'

interface Identity {
  id: string
}

export enum ImageTypes {
  SocialMedia = 'social_graphic_images',
  PersonalityPoster = 'poster_images',
  LinkedInBanner = 'banner_images',
  EmailSignature = 'signature_images',
}

type Options = {
  identity?: Identity
  type: ImageTypes
  width: number
  height: number
}

type State = {
  data?: { url: string | null; downloadUrl: string | null }
  loading?: boolean
  error?: any
}

type Query = [string, ImageTypes, string, number, number]

type UrlFetcher = ([__key, type, profileId, width, height]: Query) => Promise<{
  url: string
  downloadUrl: string
}>

type NewUrlFetcher = ([
  __key,
  type,
  profileId,
  __width,
  __height,
]: Query) => Promise<{ url: string; downloadUrl: string }>

const STATIC_IMAGE_PATH = '/static/share/images/profile-preview.png'

const URL_FETCHERS: { [key in ImageTypes]: UrlFetcher | NewUrlFetcher } = {
  [ImageTypes.EmailSignature]: newUrlFetcher,
  [ImageTypes.PersonalityPoster]: newUrlFetcher,
  [ImageTypes.LinkedInBanner]: newUrlFetcher,
  [ImageTypes.SocialMedia]: newUrlFetcher,
}

export default function useSocialImages({
  identity,
  type,
  width,
  height,
}: Options): State {
  const { data: profileId } = useProfileId({ identity: identity! })
  const query: Query | null = profileId
    ? ['useSocialImages', type, profileId, width, height]
    : null

  const {
    data,
    isLoading: urlsLoading,
    error: urlsError,
  } = useSWR(query, URL_FETCHERS[type], {
    revalidateOnFocus: false,
    keepPreviousData: true,
    fallbackData: {
      url: STATIC_IMAGE_PATH,
      downloadUrl: STATIC_IMAGE_PATH,
    },
  })

  return {
    data,
    loading: urlsLoading,
    error: urlsError,
  }
}

async function newUrlFetcher([
  __key,
  type,
  profileId,
  __width,
  __height,
]: Query): Promise<{ url: string; downloadUrl: string }> {
  return {
    url: `${enrichmentApiHost}/resources/${type}/${profileId}`,
    downloadUrl: `${enrichmentApiHost}/resources/${type}/download/${profileId}`,
  }
}
